// Curvas de crecimiento (ficha 360° → Enfermedad Actual): percentiles de
// referencia REALES — OMS 0-5 años (seed 378) y CDC 0-20 años (seed 377) en
// medical.crecimiento_referencia — + las mediciones del paciente
// (med_crecimiento). SVG propio, tema claro/oscuro:
// percentiles recesivos en un solo tono con etiqueta directa (P3…P97, P50 más
// fuerte) y la serie del paciente en color primario con puntos y tooltip.
import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Loader2 } from 'lucide-react';
import { Modal, fieldCls, med, medRpc } from './shared';
import { LOCALE } from '@/lib/utils/regional';

interface RefRow {
  indicador: string; sexo: string; edad_meses: number;
  p3: number | null; p10: number | null; p25: number | null; p50: number | null;
  p75: number | null; p90: number | null; p97: number | null;
}
interface Medicion { fecha: string; edad_meses: number; peso: number | null; talla: number | null; imc: number | null; pc: number | null }
interface Crecimiento { paciente: { genero: string | null; fecha_nacimiento: string | null }; mediciones: Medicion[] }

const INDICADORES = [
  { v: 'peso_edad', l: 'Peso para la edad', unidad: 'kg', campo: 'peso' },
  { v: 'talla_edad', l: 'Estatura para la edad', unidad: 'cm', campo: 'talla' },
  { v: 'imc_edad', l: 'IMC para la edad', unidad: 'kg/m²', campo: 'imc' },
  { v: 'pc_edad', l: 'Perímetro cefálico para la edad', unidad: 'cm', campo: 'pc' },
] as const;

// Rangos como reliv; cada indicador ofrece los que su referencia cubre
const RANGOS = [
  { v: 'oms-0-24', l: 'Nacimiento - 2 años (OMS)', min: 0, max: 24, fuente: 'OMS' },
  { v: 'oms-24-60', l: '2 - 5 años (OMS)', min: 24, max: 60, fuente: 'OMS' },
  { v: 'oms-0-60', l: 'Nacimiento - 5 años (OMS)', min: 0, max: 60, fuente: 'OMS' },
  { v: 'cdc-0-36', l: 'Nacimiento - 3 años (CDC)', min: 0, max: 36, fuente: 'CDC' },
  { v: 'cdc-24-240', l: '2 - 20 años (CDC)', min: 24, max: 240, fuente: 'CDC' },
] as const;

const PCTS: { k: keyof RefRow; l: string; fuerte?: boolean }[] = [
  { k: 'p3', l: 'P3' }, { k: 'p10', l: 'P10' }, { k: 'p25', l: 'P25' },
  { k: 'p50', l: 'P50', fuerte: true }, { k: 'p75', l: 'P75' }, { k: 'p90', l: 'P90' }, { k: 'p97', l: 'P97' },
];

export function CurvasCrecimiento({ pacienteId, nombre, genero, onClose }: {
  pacienteId: string; nombre: string; genero: string | null; onClose: () => void;
}) {
  const sexo = genero === 'F' ? 'F' : 'M';
  const [indicador, setIndicador] = useState<typeof INDICADORES[number]['v']>('peso_edad');
  const [rango, setRango] = useState<typeof RANGOS[number]['v']>('oms-0-60');
  const ind = INDICADORES.find((i) => i.v === indicador)!;
  const rg = RANGOS.find((r) => r.v === rango)!;

  // cada dataset cubre distinto: OMS = 0-60m (todos los indicadores);
  // CDC = peso/talla 0-240, IMC solo 24-240, perímetro solo 0-36
  const rangosValidos = RANGOS.filter((r) => {
    if (r.fuente === 'OMS') return true;
    if (indicador === 'imc_edad') return r.v === 'cdc-24-240';
    if (indicador === 'pc_edad') return r.v === 'cdc-0-36';
    return true;
  });
  const rangoEfectivo = rangosValidos.some((r) => r.v === rango) ? rg : rangosValidos[0];
  const fuente = rangoEfectivo.fuente;

  const { data: refRows = [], isLoading: l1 } = useQuery<RefRow[]>({
    queryKey: ['med-crecimiento-ref', indicador, sexo, fuente],
    queryFn: () => med.list<RefRow>('crecimiento_referencia', `?indicador=eq.${indicador}&sexo=eq.${sexo}&fuente=eq.${fuente}&order=edad_meses`),
    staleTime: Infinity,
  });
  const { data: crec, isLoading: l2 } = useQuery<Crecimiento>({
    queryKey: ['med-ficha', pacienteId, 'crecimiento'],
    queryFn: () => medRpc<Crecimiento>('med_crecimiento', { _paciente_id: pacienteId }),
  });

  const ref = useMemo(() => refRows.filter((r) => r.edad_meses >= rangoEfectivo.min && r.edad_meses <= rangoEfectivo.max), [refRows, rangoEfectivo]);
  const puntos = useMemo(() => (crec?.mediciones ?? [])
    .map((m) => ({ x: m.edad_meses, y: m[ind.campo] as number | null, fecha: m.fecha }))
    .filter((p): p is { x: number; y: number; fecha: string } => p.y != null && p.x >= rangoEfectivo.min && p.x <= rangoEfectivo.max),
  [crec, ind, rangoEfectivo]);

  // ---- escalas ----
  const W = 760, H = 440, M = { t: 16, r: 44, b: 40, l: 46 };
  const xMin = rangoEfectivo.min, xMax = rangoEfectivo.max;
  const yVals = [
    ...ref.flatMap((r) => PCTS.map((p) => r[p.k] as number | null)),
    ...puntos.map((p) => p.y),
  ].filter((v): v is number => v != null);
  const yMin = yVals.length ? Math.floor(Math.min(...yVals) * 0.95) : 0;
  const yMax = yVals.length ? Math.ceil(Math.max(...yVals) * 1.05) : 10;
  const X = (v: number) => M.l + ((v - xMin) / (xMax - xMin || 1)) * (W - M.l - M.r);
  const Y = (v: number) => H - M.b - ((v - yMin) / (yMax - yMin || 1)) * (H - M.t - M.b);

  const linea = (k: keyof RefRow) => ref
    .filter((r) => r[k] != null)
    .map((r, i) => `${i === 0 ? 'M' : 'L'}${X(r.edad_meses).toFixed(1)},${Y(r[k] as number).toFixed(1)}`)
    .join(' ');

  // ticks: rangos infantiles en meses (paso 3 o 6); 2-20 años cada 24 meses (etiqueta en años)
  const esInfantil = rangoEfectivo.max <= 60;
  const pasoX = !esInfantil ? 24 : rangoEfectivo.max - rangoEfectivo.min <= 24 ? 3 : 6;
  const ticksX: number[] = [];
  for (let t = xMin; t <= xMax; t += pasoX) ticksX.push(t);
  const ticksY: number[] = [];
  const pasoY = Math.max(1, Math.round((yMax - yMin) / 8));
  for (let t = yMin; t <= yMax; t += pasoY) ticksY.push(t);
  const etiquetaX = (m: number) => (esInfantil ? `${m}m` : `${Math.round(m / 12)}a`);

  const cargando = l1 || l2;
  return (
    <Modal title={`Curvas de crecimiento — ${nombre} (${sexo === 'M' ? 'niño' : 'niña'})`} onClose={onClose} wide>
      <div className="space-y-3">
        <div className="flex flex-wrap gap-2">
          <select className={`${fieldCls} w-auto`} value={indicador} onChange={(e) => setIndicador(e.target.value as typeof indicador)}>
            {INDICADORES.map((i) => <option key={i.v} value={i.v}>{i.l}</option>)}
          </select>
          <select className={`${fieldCls} w-auto`} value={rangoEfectivo.v} onChange={(e) => setRango(e.target.value as typeof rango)}>
            {rangosValidos.map((r) => <option key={r.v} value={r.v}>{r.l}</option>)}
          </select>
        </div>

        {cargando ? (
          <div className="py-16 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-primary" /></div>
        ) : (
          <div className="overflow-x-auto">
            <svg viewBox={`0 0 ${W} ${H}`} className="w-full min-w-[640px]" role="img"
              aria-label={`${ind.l}: percentiles de referencia ${fuente} y mediciones del paciente`}>
              {/* grid + ejes (recesivos) */}
              {ticksY.map((t) => (
                <g key={`y${t}`}>
                  <line x1={M.l} x2={W - M.r} y1={Y(t)} y2={Y(t)} className="stroke-border" strokeWidth="1" opacity="0.5" />
                  <text x={M.l - 6} y={Y(t) + 3} textAnchor="end" className="fill-muted-foreground" fontSize="10">{t}</text>
                </g>
              ))}
              {ticksX.map((t) => (
                <g key={`x${t}`}>
                  <line x1={X(t)} x2={X(t)} y1={M.t} y2={H - M.b} className="stroke-border" strokeWidth="1" opacity="0.3" />
                  <text x={X(t)} y={H - M.b + 14} textAnchor="middle" className="fill-muted-foreground" fontSize="10">{etiquetaX(t)}</text>
                </g>
              ))}
              <text x={M.l - 34} y={M.t + 8} className="fill-muted-foreground" fontSize="10">{ind.unidad}</text>
              <text x={(W - M.r + M.l) / 2} y={H - 6} textAnchor="middle" className="fill-muted-foreground" fontSize="10">Edad</text>

              {/* percentiles de referencia: un solo tono, P50 más fuerte, etiqueta directa */}
              {PCTS.map((p) => {
                const d = linea(p.k);
                if (!d) return null;
                const ult = [...ref].reverse().find((r) => r[p.k] != null);
                return (
                  <g key={p.k}>
                    <path d={d} fill="none" className="stroke-muted-foreground"
                      strokeWidth={p.fuerte ? 2 : 1} opacity={p.fuerte ? 0.8 : 0.45}
                      strokeDasharray={p.fuerte ? undefined : '3 3'} />
                    {ult && (
                      <text x={W - M.r + 4} y={Y(ult[p.k] as number) + 3} fontSize="9"
                        className="fill-muted-foreground" opacity={p.fuerte ? 1 : 0.8}>{p.l}</text>
                    )}
                  </g>
                );
              })}

              {/* serie del paciente: primario, línea + puntos con tooltip */}
              {puntos.length > 0 && (
                <path fill="none" className="stroke-primary" strokeWidth="2"
                  d={puntos.map((p, i) => `${i === 0 ? 'M' : 'L'}${X(p.x).toFixed(1)},${Y(p.y).toFixed(1)}`).join(' ')} />
              )}
              {puntos.map((p, i) => (
                <circle key={i} cx={X(p.x)} cy={Y(p.y)} r="4" className="fill-primary stroke-background" strokeWidth="1.5">
                  <title>{`${new Date(p.fecha).toLocaleDateString(LOCALE)} · ${p.y} ${ind.unidad} · ${p.x.toFixed(1)} meses`}</title>
                </circle>
              ))}
            </svg>
          </div>
        )}

        <div className="flex flex-wrap items-center justify-between gap-2 text-xs text-muted-foreground">
          <span>
            <span className="inline-block w-4 border-t-2 border-primary align-middle mr-1" /> Paciente ({puntos.length} medición{puntos.length === 1 ? '' : 'es'})
            <span className="inline-block w-4 border-t border-muted-foreground border-dashed align-middle ml-3 mr-1" /> Percentiles {fuente} (P3–P97)
          </span>
          <span>Fuente: {fuente === 'OMS' ? 'OMS Child Growth Standards (2006)' : 'CDC Growth Charts (2000)'}</span>
        </div>
        {puntos.length === 0 && !cargando && (
          <p className="text-sm text-primary/80">No hay mediciones del paciente para este indicador — registra peso/talla/perímetro cefálico en los signos vitales de las consultas.</p>
        )}
      </div>
    </Modal>
  );
}
